import { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Rating,
  Typography,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { generatePath, Link, useParams } from "react-router-dom";
import {
  getProductReviews,
  getProduct,
} from "../../utility/httpActions/productActions";
import {
  getShopReviews,
  getPublicShop,
} from "../../utility/httpActions/shopActions";
import {
  likeReview,
  unlikeReview,
} from "../../utility/httpActions/reviewActions";
import ImageWithLoader from "../../common-components/ImageWithLoader";
import VerifiedBadge from "../../common-components/VerifiedBadge";
import { resolveImageUri } from "../../utility/constants";
import { frontendRoutes } from "../../utility/frontendRoutes";
import useAuthentication from "../../hooks/useAuthentication";

const LIKE_ACTIVE_COLOR = "#046287";
const LIKE_INACTIVE_COLOR = "#4B5563";

const ReviewLikeButton = ({
  reviewId,
  likesCount = 0,
  isLikedByUser = false,
}) => {
  const { isLoggedIn } = useAuthentication();
  const queryClient = useQueryClient();
  const [optimistic, setOptimistic] = useState({
    liked: isLikedByUser,
    count: likesCount,
  });

  const { mutate } = useMutation({
    mutationFn: (currentlyLiked) =>
      currentlyLiked ? unlikeReview(reviewId) : likeReview(reviewId),
    onMutate: (currentlyLiked) => {
      setOptimistic({
        liked: !currentlyLiked,
        count: currentlyLiked ? optimistic.count - 1 : optimistic.count + 1,
      });
    },
    onError: () => setOptimistic({ liked: isLikedByUser, count: likesCount }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["productReviews", "shopReviews"],
      }),
  });

  const color = optimistic.liked ? LIKE_ACTIVE_COLOR : LIKE_INACTIVE_COLOR;

  return (
    <Box
      onClick={() => {
        if (isLoggedIn) mutate(optimistic.liked);
      }}
      title={
        isLoggedIn ? (optimistic.liked ? "Unlike" : "Like") : "Sign in to like"
      }
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        cursor: isLoggedIn ? "pointer" : "default",
        py: "2px",
        userSelect: "none",
        width: "fit-content",
      }}
    >
      {optimistic.liked ? (
        <ThumbUpIcon sx={{ fontSize: 17, color }} />
      ) : (
        <ThumbUpOutlinedIcon sx={{ fontSize: 17, color }} />
      )}
      {optimistic.count > 0 && (
        <Typography
          sx={{ fontSize: 13, color, fontWeight: 500, lineHeight: 1 }}
        >
          {optimistic.count}
        </Typography>
      )}
    </Box>
  );
};

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "rating_high", label: "Highest rated" },
  { value: "rating_low", label: "Lowest rated" },
];

const STAR_FILTERS = [
  { value: null, label: "All" },
  { value: "5", label: "5★" },
  { value: "4", label: "4★" },
  { value: "3", label: "3★" },
  { value: "2", label: "2★" },
  { value: "1", label: "1★" },
];

const PAGE_SIZE = 15;

const formatDate = (dateStr) =>
  dateStr
    ? new Date(dateStr).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

// ── Review card ───────────────────────────────────────────────────

const ReviewCard = ({ review, productId }) => {
  // For shop reviews: use shopRating; for product reviews: prefer product-specific rating
  const productRating = productId
    ? review.productRatings?.find(
        (pr) => (pr.product?.id || pr.product) === productId,
      )
    : null;
  const rating = productRating?.rating ?? review.shopRating ?? 0;
  const text = productRating?.text || review.shopText;

  const userName = review.user?.name || "Buyer";
  const avatarSrc = review.user?.profilePhoto
    ? resolveImageUri(review.user.profilePhoto)
    : undefined;
  const date = formatDate(review.createdAt);
  const photos =
    review.media?.filter((m) => m.type === "image").map((m) => m.url) ?? [];

  return (
    <Box
      sx={{
        py: 2.5,
        borderBottom: "1px solid #f3f4f6",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      {/* Reviewer row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <Avatar
          src={avatarSrc}
          sx={{ width: 36, height: 36, bgcolor: "#046287", fontSize: 13 }}
        >
          {userName[0]?.toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            {userName}
            {review.user?.isIdentityVerified && <VerifiedBadge size={13} />}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {date}
          </Typography>
        </Box>
        <Rating value={rating} precision={0.5} readOnly size="small" />
      </Box>

      {/* Tags */}
      {review.tags?.length > 0 && (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
          {review.tags.map((t) => (
            <Chip
              key={t}
              label={t}
              size="small"
              sx={{ fontSize: 11, height: 22 }}
            />
          ))}
        </Box>
      )}

      {/* Text */}
      {text && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.7, mb: 1 }}
        >
          {text}
        </Typography>
      )}

      {/* Per-product ratings on shop review pages */}
      {!productId && review.productRatings?.length > 0 && (
        <Box sx={{ pl: 1.5, borderLeft: "2px solid #f3f4f6", mt: 1 }}>
          {review.productRatings.map((pr, i) => (
            <Box
              key={i}
              sx={{ mt: 1, display: "flex", flexDirection: "column" }}
            >
              {pr.product && (
                <Typography
                  component={Link}
                  to={generatePath(frontendRoutes.VIEW_PRODUCT, {
                    productId: pr.product?.id || pr.product,
                  })}
                  variant="caption"
                  sx={{
                    color: "#046287",
                    fontWeight: 600,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                    display: "block",
                  }}
                >
                  {pr.productName}
                </Typography>
              )}
              {pr.rating > 0 && (
                <Rating
                  value={pr.rating}
                  precision={0.5}
                  readOnly
                  size="small"
                  sx={{ mt: 0.25 }}
                />
              )}
              {pr.text && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {pr.text}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Photos */}
      {photos.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
          {photos.slice(0, 5).map((url, i) => (
            <Box
              key={i}
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "#f5f5f5",
                flexShrink: 0,
              }}
            >
              <ImageWithLoader src={url} alt="" />
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 1, ml: 1 }}>
        <ReviewLikeButton
          reviewId={review._id || review.id}
          likesCount={review.likesCount ?? 0}
          isLikedByUser={review.isLikedByUser ?? false}
        />
      </Box>
    </Box>
  );
};

// ── Page ──────────────────────────────────────────────────────────

const AllReviewsPage = ({ entityType }) => {
  const { productId, shopId } = useParams();
  const id = productId || shopId;
  const isProduct = entityType === "product";

  const [sort, setSort] = useState("newest");
  const [starFilter, setStarFilter] = useState(null);
  const sentinelRef = useRef(null);

  const fetcher = isProduct ? getProductReviews : getShopReviews;
  const queryParams = {
    limit: PAGE_SIZE,
    sort,
    ...(starFilter ? { starFilter } : {}),
  };

  // Fetch the entity for its rating — cached instantly if coming from the product/shop page
  const { data: entityData } = useQuery({
    queryKey: isProduct ? ["product", id] : ["publicShop", id],
    queryFn: () => (isProduct ? getProduct(id) : getPublicShop(id)),
    enabled: !!id,
  });
  const overallRating = entityData?.rating ?? 0;
  const entityTotalReviews = entityData?.numReviews ?? 0;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [isProduct ? "productReviews" : "shopReviews", id, queryParams],
      queryFn: ({ pageParam = 1 }) =>
        fetcher(id, { ...queryParams, page: pageParam }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
      enabled: !!id,
      retry: false,
    });

  const reviews = useMemo(
    () => data?.pages?.flatMap((p) => p.items) ?? [],
    [data],
  );
  const firstPage = data?.pages?.[0];
  const totalReviews = firstPage?.total ?? entityTotalReviews;

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2, md: 4 }, py: 3 }}>
      {/* Summary header */}
      {(overallRating > 0 || totalReviews > 0) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            pb: 2,
            borderBottom: "1px solid #f3f4f6",
          }}
        >
          <Rating value={overallRating} precision={0.1} readOnly />
          <Typography variant="body1" fontWeight={700}>
            {Number(overallRating).toFixed(1)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ({totalReviews} {totalReviews === 1 ? "review" : "reviews"})
          </Typography>
        </Box>
      )}

      {/* Star filter chips */}
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
        {STAR_FILTERS.map((f) => (
          <Chip
            key={String(f.value)}
            label={f.label}
            size="small"
            onClick={() => setStarFilter(f.value)}
            sx={{
              cursor: "pointer",
              bgcolor: starFilter === f.value ? "#046287" : undefined,
              color: starFilter === f.value ? "white" : undefined,
              "&:hover": starFilter === f.value ? { bgcolor: "#046287" } : {},
            }}
          />
        ))}
      </Box>

      {/* Sort chips */}
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2.5 }}>
        {SORT_OPTIONS.map((s) => (
          <Chip
            key={s.value}
            label={s.label}
            size="small"
            variant="outlined"
            onClick={() => setSort(s.value)}
            sx={{
              cursor: "pointer",
              borderColor: sort === s.value ? "#046287" : undefined,
              color: sort === s.value ? "#046287" : undefined,
              fontWeight: sort === s.value ? 700 : 400,
            }}
          />
        ))}
      </Box>

      {/* List */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#046287" }} />
        </Box>
      ) : reviews.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography color="text.secondary">
            {starFilter ? `No ${starFilter}★ reviews yet.` : "No reviews yet."}
          </Typography>
        </Box>
      ) : (
        <>
          <Box>
            {reviews.map((review, i) => (
              <ReviewCard
                key={review._id || review.id || i}
                review={review}
                productId={isProduct ? productId : null}
              />
            ))}
          </Box>
          <Box
            ref={sentinelRef}
            sx={{ py: 4, display: "flex", justifyContent: "center" }}
          >
            {isFetchingNextPage && (
              <CircularProgress size={28} sx={{ color: "#046287" }} />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default AllReviewsPage;
